import { useAuthStore } from "@/stores/authStore";
import { env } from "@/config/env";

const DEV_API_URL = "http://localhost:8000";

function getBaseUrl(): string {
  return env.isDev ? DEV_API_URL : env.aws.apiUrl;
}

async function adminFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const method = options.method?.toUpperCase() ?? "GET";
  const headers: Record<string, string> = {
    ...(method !== "GET" && method !== "HEAD" ? { "Content-Type": "application/json" } : {}),
    ...(options.headers as Record<string, string>),
  };

  if (!env.isDev) {
    const token = await useAuthStore.getState().getIdToken();
    headers.Authorization = token ?? "";
  }

  const res = await fetch(`${getBaseUrl()}${path}`, { ...options, headers });

  // Retry once on 401 (token may have expired)
  if (res.status === 401 && !env.isDev) {
    const freshToken = await useAuthStore.getState().getIdToken();
    if (!freshToken) {
      await useAuthStore.getState().logout();
      throw new Error("Session expired");
    }
    headers.Authorization = freshToken;
    return fetch(`${getBaseUrl()}${path}`, { ...options, headers });
  }

  return res;
}

/* ── Types ── */

export interface DashboardUser {
  email: string;
  user_id: string;
  tier: string;
  status: "active" | "invited" | "disabled";
  created: string;
  last_active: string | null;
  messages_7d: number;
  sparkline: number[];
  health: "growing" | "steady" | "declining" | "churning";
  has_friction: boolean;
  /** true = Combify MCP account, managed in the MCP tab */
  mcp: boolean;
  mcp_today: number;
  mcp_30d: number;
  mcp_last_active: string | null;
}

export interface AttentionItem {
  type: string;
  user_email?: string;
  summary: string;
  timestamp?: string;
}

export interface DashboardData {
  pulse: {
    messages_today: number;
    users_today: number;
    messages_this_week: number;
    messages_last_week: number;
    trend_pct: number;
    mcp_calls_today?: number;
    mcp_users_today?: number;
  };
  attention: AttentionItem[];
  opportunities: AttentionItem[];
  users: DashboardUser[];
}

export interface ToolCall {
  name?: string;
  input?: string;
  result?: string;
  is_error?: boolean;
}

export interface ThreadMessage {
  role: string;
  content: string;
  tools: ToolCall[];
  created_at: string;
}

export interface ConversationThread {
  conversation_id: string;
  messages: ThreadMessage[];
  truncated: boolean;
  total_messages: number;
}

export interface UserHealth {
  email: string;
  user_id: string;
  total_conversations: number;
  health: "growing" | "steady" | "declining" | "churning";
  metrics: {
    messages_this_week: number;
    messages_last_week: number;
    trend_pct: number;
    tools_used: number;
    tools_total: number;
    tools_label: string;
    avg_conversation_length: number;
    conv_label: string;
    friction_count: number;
  };
  top_tools: { name: string; count: number }[];
  recent_conversations: {
    conversation_id: string;
    title: string;
    message_count: number;
    updated_at: string;
  }[];
}

/* ── Endpoints ── */

export async function fetchDashboard(): Promise<DashboardData> {
  const res = await adminFetch("/admin/dashboard");
  if (res.status === 403) throw new Error("Forbidden");
  if (!res.ok) throw new Error("Failed to fetch dashboard");
  return res.json() as Promise<DashboardData>;
}

export async function fetchUserHealth(userId: string): Promise<UserHealth> {
  const res = await adminFetch(`/admin/user/${encodeURIComponent(userId)}`);
  if (res.status === 403) throw new Error("Forbidden");
  if (!res.ok) throw new Error("Failed to fetch user health");
  return res.json() as Promise<UserHealth>;
}

export async function fetchConversationThread(conversationId: string): Promise<ConversationThread> {
  const res = await adminFetch(`/admin/conversation/${encodeURIComponent(conversationId)}`);
  if (res.status === 403) throw new Error("Forbidden");
  if (!res.ok) throw new Error("Failed to fetch conversation");
  return res.json() as Promise<ConversationThread>;
}

export interface InvitedUser {
  email: string;
  tier: string;
  status: string;
  /** Account was created but the invite email could not be sent. */
  email_failed?: boolean;
}

export async function inviteUser(email: string, tier: string, mcp: boolean): Promise<InvitedUser> {
  const res = await adminFetch("/admin/users", {
    method: "POST",
    body: JSON.stringify({ email, tier, mcp }),
  });
  if (res.status === 409) throw new Error("User already exists");
  if (res.status === 403) throw new Error("Forbidden");
  if (!res.ok) throw new Error("Failed to invite user");
  return res.json();
}

/** Send the Swedish MCP invite (set-password link + Claude setup) again. */
export async function resendMcpInvite(email: string): Promise<void> {
  const res = await adminFetch("/admin/users", {
    method: "POST",
    body: JSON.stringify({ email, action: "resend_mcp_invite" }),
  });
  if (res.status === 403) throw new Error("Forbidden");
  if (res.status === 404) throw new Error("User not found");
  if (res.status === 400) throw new Error("Not an MCP account");
  if (!res.ok) throw new Error("Failed to send invite");
}

export async function enableUser(email: string): Promise<void> {
  const res = await adminFetch("/admin/users", {
    method: "POST",
    body: JSON.stringify({ email, action: "enable" }),
  });
  if (res.status === 403) throw new Error("Forbidden");
  if (!res.ok) throw new Error("Failed to enable user");
}

export async function disableUser(email: string): Promise<void> {
  const res = await adminFetch(`/admin/users/${encodeURIComponent(email)}`, {
    method: "DELETE",
  });
  if (res.status === 403) throw new Error("Forbidden");
  if (!res.ok) throw new Error("Failed to disable user");
}
